import {
  getLeoUuid,
  LeoUUID,
  getString,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class GetSurveyViewFilterOptionsRPC implements
  LeoRPC<GetSurveyViewFilterOptionsRPC.Request, GetSurveyViewFilterOptionsRPC.Response, GetSurveyViewFilterOptionsRPC.Errors.Errors>
{
  abstract execute(
    request: GetSurveyViewFilterOptionsRPC.Request
  ): Promise<LeoRPCResult<GetSurveyViewFilterOptionsRPC.Response, GetSurveyViewFilterOptionsRPC.Errors.Errors>>;
}

export namespace GetSurveyViewFilterOptionsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    surveyViewId: string;
    filter: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetSurveyViewFilterOptionsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly surveyViewId: LeoUUID;
    readonly filter: string;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    surveyViewId: LeoUUID, 
    filter: string
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.surveyViewId = surveyViewId;
      Request.validateFilter(filter);
      this.filter = filter;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const surveyViewId = getLeoUuid(dto, "surveyViewId", InvalidRequestSurveyViewIdError);
      const filter = getString(dto, "filter", InvalidRequestFilterError);
      return new Request(
        projectId,
        surveyId,
        surveyViewId,
        filter
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          surveyViewId: this.surveyViewId.toDTO(),
          filter: this.filter
      };
      return dto;
    }
    private static validateFilter = (filter: string): void => {
      if(filter.trim().length === 0) {
        throw new InvalidRequestFilterNonEmptyError(
          `Attribute filter cannot be an empty string.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetSurveyViewFilterOptionsRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',surveyViewId='${this.surveyViewId}',filter='${this.filter}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    filterOptions: string[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetSurveyViewFilterOptionsResponse: boolean = true;
  
    readonly filterOptions: string[];
  
  
    constructor(
    filterOptions: string[]
    ) {
      this.filterOptions = filterOptions;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const filterOptions = getList<string, string>(
        dto,
        "filterOptions",
        (e: string) => e,
        InvalidResponseFilterOptionsError
      );
      return new Response(
        filterOptions
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          filterOptions: this.filterOptions
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetSurveyViewFilterOptions(filterOptions='${this.filterOptions}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidSurveyViewId = "INVALID_SURVEY_VIEW_ID",
    TooManyUniqueValues = "TOO_MANY_UNIQUE_VALUES"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetSurveyViewFilterOptionsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetSurveyViewFilterOptionsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidSurveyViewIdDTO {
      code: string;
    }
    
    export class InvalidSurveyViewId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyViewId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyViewId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyViewIdDTO {
        const dto: InvalidSurveyViewIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyViewId.prototype.toString = function toString(): string {
      return `GetSurveyViewFilterOptionsRPC.INVALID_SURVEY_VIEW_ID()`;
    };
    
    
    export interface TooManyUniqueValuesDTO {
      code: string;
    }
    
    export class TooManyUniqueValues extends LeoRPCError<RPCError> {
      readonly isTooManyUniqueValues: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.TooManyUniqueValues;
        super(code);
      }
    
    
      toDTO(): TooManyUniqueValuesDTO {
        const dto: TooManyUniqueValuesDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    TooManyUniqueValues.prototype.toString = function toString(): string {
      return `GetSurveyViewFilterOptionsRPC.TOO_MANY_UNIQUE_VALUES()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | InvalidSurveyViewId | TooManyUniqueValues;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | InvalidSurveyViewIdDTO | TooManyUniqueValuesDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidSurveyViewId:
          return new InvalidSurveyViewId();
        case RPCError.TooManyUniqueValues:
          return new TooManyUniqueValues();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetSurveyViewFilterOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends GetSurveyViewFilterOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestSurveyViewIdError extends GetSurveyViewFilterOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyViewIdError";
  }
}

export class InvalidRequestFilterError extends GetSurveyViewFilterOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFilterError";
  }
}

export class InvalidRequestFilterNonEmptyError extends InvalidRequestFilterError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFilterNonEmptyError";
  }
}

export class InvalidResponseFilterOptionsError extends GetSurveyViewFilterOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseFilterOptionsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetSurveyViewFilterOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetSurveyViewFilterOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidSurveyViewIdError extends GetSurveyViewFilterOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyViewIdError";
  }
}

export class InvalidResponseTooManyUniqueValuesError extends GetSurveyViewFilterOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTooManyUniqueValuesError";
  }
}

